import moment from "moment";
import { commonValueFormatter, formatNum, formatPercentNum, unitConversion } from "@/pages/dashboard/utils/utils";
import { CHART_FONT_FAMILY, DARK_COLOR_ARR, LIGHT_COLOR_ARR } from "./constant";
import { ChartOption, LineChartProps, TChartTooltipsConfig, formatConfigType } from "./interface";
import { getEchartTooltipText } from "@/pages/dashboard/components/EchartsBubble";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";

import '../chart.scss';
import { getSumValue } from "@/pages/dashboard/pages/overview/components/MetricsUserCompositionTrendsCo/_hooks";

export const makeupOptions = (props: LineChartProps, chartType: string) => {
  const {
    formatterFunc,
    isDark,
    legendData,
    selectedLegendData = {},
    colorArr,
    series,
    xAxisData,
    setOtherConfigFunc,
    isShowLegend = true,
    isShowTooltip = true,
    barMaxWidth = 80,
    isMultipleCompetitor = false,
    splitNum = 24,
  } = props;

  let preIndex = 0;
  const intervalFunc = (index: number) => {
    const count = xAxisData?.length;
    const defaultInterval = parseInt(((count - 1) / splitNum).toString());
    let differNum = count - defaultInterval * splitNum - 1;
    if (index === count - 1) {
      // 遍历完数据重置（防止数据被刷掉）
      differNum = count - defaultInterval * splitNum - 1;
      preIndex = 0;
      return true;
    }
    if (index === 0) {
      return true;
    }
    // 间隔数
    const currentInterval = differNum > 0 ? defaultInterval : defaultInterval - 1;
    if (preIndex + 1 + currentInterval === index) {
      differNum = differNum > 0 ? differNum - 1 : 0;
      preIndex = index;
      return true;
    }
    return false;
  };

  const opts: ChartOption = {
    textStyle: {
      fontFamily: CHART_FONT_FAMILY
    },
    tooltip: {
      show: isShowTooltip,
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      borderWidth: 0,
      borderRadius: 6,
      textStyle: {
        color: '#FFFFFF',
        fontSize: 12,
      },
      axisPointer: {
        type: chartType === 'line' ? 'line' : 'shadow',
      },
      confine: true,
      hideDelay: 200,
      triggerOn: 'mousemove',
      padding: 0,
      formatter: formatterFunc ? formatterFunc : null
    },
    grid: {
      x: 2,
      y: 20,
      x2: 10,
      y2: 30,
      containLabel: true
    },
    legend: isMultipleCompetitor ? legendData :
      {
        show: isShowLegend,
        type: 'scroll',
        data: legendData,
        selectedMode: true,
        orient: 'horizontal',
        itemHeight: 8,
        itemWidth: 8,
        icon: 'circle',
        bottom: -6,
        itemGap: 18,
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end',// start
        left: 'center',
        textStyle: { // 图例文字的样式
          color: '#838697',
          fontSize: 12,
          lineHeight: 16,
        },
        selected: selectedLegendData,
      },
    color: colorArr ? colorArr : isDark ? DARK_COLOR_ARR : LIGHT_COLOR_ARR,
    xAxis: [{
      type: 'category',
      data: xAxisData,
      interval: 1,
      axisLabel: {
        color: '#838697',
        fontSize: 10,
        align: 'center',
        interval: !isMultipleCompetitor ? 'auto' : (index) => intervalFunc(index),
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
        interval: !isMultipleCompetitor ? 'auto' : (index) => intervalFunc(index),
      },
      axisLine: {
        lineStyle: {
          color: '#D8DAE4',
        },
        show: true
      },
      axisPointer: {
        type: chartType === 'line' ? 'line' : 'shadow',
      },
    }],
    axisLabel: {
      showMinLabel: true, // 强制显示最小值
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          color: '#ddd',
        },
      },
      axisLabel: {
        formatter: (value: number) => yAxisFormat(value),
        color: '#838697',
        fontSize: 12,
      },
    },
    series,
    barMaxWidth,
  };
  return setOtherConfigFunc ? setOtherConfigFunc(opts) : opts;


};

/**
 * 折线图浮窗展示：
 * @param param 参数
 * @param title 标题
 * @param isPercent 是否百分比
 * @param isFloating 数值类型是否保留两位小数
 * @returns
 */
export const lineFormatterFunc = (param: any, title: any, isPercent = false, isFloating = false,) => {
  let returnStr = `<div class=${'basic-formatter-box'}>
          <div class=${'basic-formatter-header'}>
          <p class='basic-formatter-one basic-formatter-title'>${param[0].axisValue}</p>
          <p class='basic-formatter-two basic-formatter-title'>${title}</p></div>`;
  for (let i = 0;i < param.length;i++) {
    // realtime最新值动效类型特殊处理
    if (param?.[i]?.componentSubType === 'effectScatter' && param?.[i]?.seriesType === 'effectScatter') break;
    const dauValue = isPercent ? formatPercentNum(param[i].value, 2, false) : formatNum(param[i].value, false, !isFloating);
    const name = param[i].seriesName;
    returnStr += `<div class=${'basic-formatter-header'}>
          <p class=${'basic-formatter-one'}>${param[i].marker}&nbsp;${name}</p>
          <p class=${'basic-formatter-two'}>${dauValue}</p></div>`;
  }
  returnStr = `${returnStr}`;
  return returnStr;
};

export const barFormatterFunc = (param: any[], config?: formatConfigType) => {
  let title = '';

  // 浮窗数据是否排序
  const { isSort, isShowWeekDay, isRemoveNull, isPercent = false, isDollar = false,
    isUserQuota = false, metricsName = '', type = '', decimal ='', weekDayArr } = config || {};
  const data = param?.map(item => {
    if (!title) title = item.axisValue;
    if(type && decimal) {
      return {
        name: item.seriesName,
        metricsNum: commonValueFormatter(type, item.value, decimal),
        color: item.color
      };
    }
    return {
      name: item.seriesName,
      metricsNum: isPercent ? formatPercentNum(item.value) : formatNum(item.value, isDollar, isUserQuota),
      color: item.color
    };
  });
  return getEchartTooltipText({
    metricsName,
    title,
    isShowWeekDay,
    weekDayArr,
    data,
    isSort,
    isRemoveNull
  });
};

export const pieFormatterFunc = (params: any, formValue: any, config?: any) => {
  const { name, value, color, data: { total } } = params;
  const { type, decimal, chartPercentDecimal, isLastDate = true } = config || {};
  const timeTypeObj: any = {
    Day: 'YYYY-MM-DD',
    Week: 'YYYY-MM-DD',
    Month: 'YYYY-MM'
  };
  const { timeType = 'Day', filterValue } = formValue;
  const { rangeTime = [] } = filterValue ?? {};
  const [starTime, endTime] = rangeTime;
  const pieTitle = isLastDate ? moment(endTime).format(timeTypeObj[timeType]) : `${moment(starTime).format(timeTypeObj[timeType])  }~${  moment(endTime).format(timeTypeObj[timeType])}`;
  return getEchartTooltipText({
    title: pieTitle,
    metricsName: '',
    ratioName: '',
    hasRatio: true,
    data: [{
      name,
      metricsNum: (type && decimal)? commonValueFormatter(type, value, decimal) : formatNum(value, false, true),
      ratioNum: formatPercentNum(value / total, chartPercentDecimal ? chartPercentDecimal : 1),
      color
    }]
  });
};

export const yAxisFormat = (value: number, isPercent = false, fixedNumber = 2, isRetention = false) => {
  // TOFIX: 有关 value < 0 ? '-' : '' 都是临时处理
  if (isRetention) {
    if (value > 0 && value < 0.1 && String(value).length > 4) return `${value < 0 ? '-' : ''}${formatPercentNum(+value, 1)}`;
    return `${value < 0 ? '-' : ''}${formatPercentNum(value, 0)}`;
  }
  const formatLabel = value ? unitConversion(value, true) : value;
  let num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
  if (isPercent) {
    if (value === 0) {
      num = "0%";
    }
    num = `${value < 0 ? '-' : ''}${formatPercentNum(value, fixedNumber)}`;
  }
  return num;
};

export const getMin = (value: { min: number; max: number; }) => {
  const { min, max } = value;
  // 刻度间隔
  const boundaryGap = (max - min) / 5;
  if (boundaryGap > 1) {
    const minLen = (min.toFixed(0)).length;
    const gapLen = (boundaryGap.toFixed(0)).length;
    const len = minLen - gapLen;
    const pow = Math.pow(10, gapLen - 1);
    if (len === 0 || len === 1) {
      return Math.floor(min / pow) * pow;
    }
  }
  const num = min > 1 ? Math.floor(min) : min;
  return num > min ? Math.floor(min) : num;
};

export const legendSelectChanged = (_chart: any) => {
  // realtime 图例取消联动最新点特效功能
  _chart.on('legendselectchanged', (params: any) => {
    if (_chart) {
      const options = _chart.getOption();
      const series: any = options?.series || [];
      const isSelected = params?.selected?.[series[0].name];
      series[3].data?.map((item: any) => {
        if (typeof item === 'object') {
          item.label.show = isSelected;
        }
      });
      series[3].symbolSize = isSelected ? 6 : 0;
      if (options) _chart?.setOption(options, true);
    }
  });
};

/**
 * echarts tooltip 数据排序
 * seriesName 为 other 则不参与排序
 * @param data
 * @param config
 */
export const formatterTooltipSort = (data: Object[], config: { sortType?: 'asc' | 'desc'; sortKey: string; }) => {
  const { sortType, sortKey } = config;
  const others = data.filter((item: any) => ['其他', 'others'].includes(item[sortKey]));
  return data.filter((item: any) => ['其他', 'others'].includes(item[sortKey])).sort((a: any, b: any) => sortType === 'asc' ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]).concat(others);
};


// 新版浮窗处理组件，调用新的dataFormat；
export const chartTooltiosFormatter = (param: any[], config?: TChartTooltipsConfig) => {
  let title = '';
  // 浮窗数据是否排序
  const { isSort = true, isShowWeekDay = true, weekDayArr, isRemoveNull,  metricsName = '', format = 'thousandsInit', sumName, hasSum=false} = config || {};
  const data = param?.map(item => {
    if (!title) title = item.axisValue;
    return {
      name: item.seriesName,
      metricsNum: dataFormat({value: item.value, formatRule: format}),
      color: item.color
    };
  });
  return getEchartTooltipText({
    metricsName,
    title,
    isShowWeekDay,
    weekDayArr,
    data,
    isSort,
    isRemoveNull,
    hasSum,
    sumNum: getSumValue(param, false),
    sumName,
  });
};
